/**
 * Live-Ansicht des Autonomie-Laufs (B12a, 28.10.2026) — was Server (server/lauf-ansicht.ts) und Seite (frontend/lib/lauf-leiste.ts)
 * gemeinsam wissen.
 *
 * Martin: er sieht nicht, ob ein Lauf (Mandat, Etappen-Lauf) läuft, wartet, abgelaufen oder gestoppt ist. Die Ansicht sagt es in einem Satz.
 *
 * ⚠️ Nur feste Kürzel und Zahlen gehen über die Leitung: Stand, Stufe, Art, Restminuten, Schrittzahl, letzter Schritt (Kürzel aus der festen
 *    Liste unten), Kosten in Cent, zwei Zeitpunkte, Grund (Kürzel). Nie ein Seitentext, eine Adresse, ein Wert, ein Werkzeugname oder
 *    Martins Wortlaut. Die Sätze baut die Seite aus den Tabellen hier — ein unbekanntes Kürzel macht die ganze Antwort ungültig.
 * ⚠️ Kein Ja: die Ansicht hat genau EINE Handlung, den Stopp-Knopf (vorhandene Route /api/chat/abort mit `{ stopp: true }`).
 */
import { MANDAT_ARTEN, MANDAT_STUFEN, MANDAT_STUFEN_WERTE, type MandatArt, type MandatStufe } from "./prozess-freigabe.js";

export const LAUF_STAENDE = [
  "arbeitet",
  "wartet-auf-martin",
  /** B12b: der Lauf wartet auf Martins Anmeldung (server/anmelde-warten.ts) — die Uhr der Stufe steht. */
  "wartet-auf-anmeldung",
  "limit",
  "abgelaufen",
  "gestoppt",
  "fertig",
  "kein-lauf",
] as const;
export type LaufAnsichtStand = (typeof LAUF_STAENDE)[number];

/** Der letzte Schritt als festes Kürzel — je Werkzeugklasse, nie der Werkzeugname. */
export const LAUF_SCHRITTE = ["seite-geoeffnet", "seite-gelesen", "geklickt", "eingetippt", "tab-gewechselt", "ablauf", "anderer-schritt"] as const;
export type LaufSchritt = (typeof LAUF_SCHRITTE)[number];

/** Warum ein Lauf steht oder endete — festes Kürzel. */
export const LAUF_GRUENDE = [
  "zeit",
  "stopp",
  "fertig",
  "fehler",
  "stillstand",
  "rahmen",
  "rot",
  "ja-tor",
  "aussen",
  "geld",
  "fremd",
  "kosten",
  "budget",
  "tageslimit",
  "deckel",
  "anderer-zug",
  "neustart",
  "aus",
  /** B12b: das Warten auf die Anmeldung endete ohne Erkennung (Höchstwartezeit, Gerät weg) bzw. die Anmeldung hielt nicht. */
  "anmeldung",
  /** B12b (M5): der Lauf kam nach dem Tipp nicht in Gang. */
  "start",
  "sonst",
] as const;
export type LaufGrund = (typeof LAUF_GRUENDE)[number];

/** Die Antwort von GET /api/lauf. */
export interface LaufBlick {
  v: 1;
  stand: LaufAnsichtStand;
  /** Mandats-Stufe — `null` ohne Mandat (Etappen-Lauf aus einer Prozess-Freigabe). */
  stufe: MandatStufe | null;
  art: MandatArt | null;
  /** Restzeit des Mandats in ganzen Minuten (aufgerundet) — `null` ohne Mandat oder nach dem Ende. */
  restMin: number | null;
  schritte: number;
  letzterSchritt: LaufSchritt | null;
  kostenCent: number | null;
  budgetCent: number | null;
  /** Letzte Bewegung (ms seit 1970) — `null`: noch keine. */
  letzteMs: number | null;
  /** Ende (ms seit 1970) — nur bei abgelaufen, gestoppt, fertig. */
  endeMs: number | null;
  grund: LaufGrund | null;
  /** B12b: seit wie vielen ganzen Minuten der Lauf auf Martins Anmeldung wartet — `null`: er wartet nicht. */
  seitMin: number | null;
}

export const KEIN_LAUF: Readonly<LaufBlick> = Object.freeze({
  v: 1,
  stand: "kein-lauf",
  stufe: null,
  art: null,
  restMin: null,
  schritte: 0,
  letzterSchritt: null,
  kostenCent: null,
  budgetCent: null,
  letzteMs: null,
  endeMs: null,
  grund: null,
  seitMin: null,
});

/** Ein beendeter Lauf bleibt so lange als letzter Stand sichtbar (24 Std) — damit „nichts passiert“ erklärbar bleibt. */
export const LAUF_NACHLAUF_MS = 24 * 60 * 60_000;

/** So oft fragt die offene Seite nach (ms). */
export const LAUF_ABFRAGE_MS = 5000;

const BROWSER_KLASSEN: Readonly<Record<string, LaufSchritt>> = Object.freeze({
  browser_open_url: "seite-geoeffnet",
  browser_get_page_text: "seite-gelesen",
  browser_get_page_html: "seite-gelesen",
  browser_get_selection: "seite-gelesen",
  browser_form_overview: "seite-gelesen",
  browser_screenshot: "seite-gelesen",
  browser_get_active_tab: "seite-gelesen",
  browser_list_tabs: "seite-gelesen",
  tab_context: "seite-gelesen",
  browser_click: "geklickt",
  browser_click_auswahl: "geklickt",
  browser_hover: "geklickt",
  browser_drag: "geklickt",
  browser_type: "eingetippt",
  browser_form_fill: "eingetippt",
  browser_keypress: "eingetippt",
  browser_press_key: "eingetippt",
  browser_switch_tab: "tab-gewechselt",
  browser_ablauf: "ablauf",
});

/** Werkzeugname → festes Kürzel. Unbekanntes (auch mit Vorsatz wie `mcp__x__`) wird „anderer-schritt“ — der Name selbst geht nie hinaus. */
export function schrittKuerzel(werkzeug: unknown): LaufSchritt {
  if (typeof werkzeug !== "string") return "anderer-schritt";
  const n = werkzeug.slice(werkzeug.lastIndexOf("__") + 1).replace(/^_/, "");
  return Object.hasOwn(BROWSER_KLASSEN, n) ? (BROWSER_KLASSEN[n] as LaufSchritt) : "anderer-schritt";
}

// ---------------------------------------------------------------------------------------------------------------------------------
// Sätze (nur aus festen Tabellen)
// ---------------------------------------------------------------------------------------------------------------------------------

/** Wie die Seite heißt, auf der der Lauf arbeitet — aus der Art, nie aus einer Adresse. */
const SEITE_JE_ART: Readonly<Record<MandatArt, string>> = Object.freeze({
  "aws-ses": "AWS-Seite",
  webseite: "Webseite",
  "video-test": "Video-Seite",
  backup: "Sicherungs-Seite",
  "wachwort-quote": "Mess-Seite",
  "allgemein-browser": "Seite",
  "soniox-einrichtung": "Soniox-Seite",
});

const SCHRITT_SATZ: Readonly<Record<LaufSchritt, (seite: string) => string>> = Object.freeze({
  "seite-geoeffnet": (s: string) => `${s} geöffnet`,
  "seite-gelesen": (s: string) => `${s} geprüft`,
  geklickt: (s: string) => `auf der ${s} geklickt`,
  eingetippt: (s: string) => `Eingabe auf der ${s}`,
  "tab-gewechselt": () => "Tab gewechselt",
  ablauf: (s: string) => `Ablauf auf der ${s}`,
  "anderer-schritt": () => "Schritt erledigt",
});

export const GRUND_SATZ: Readonly<Record<LaufGrund, string>> = Object.freeze({
  zeit: "Wartezeit zählte mit",
  stopp: "von dir gestoppt",
  fertig: "Ergebnis steht im Chat",
  fehler: "zu viele Fehlversuche",
  stillstand: "kein Fortschritt mehr",
  rahmen: "Seite außerhalb des Rahmens",
  rot: "gesperrter Schritt",
  "ja-tor": "braucht dein Ja im Chat",
  aussen: "Schritt mit Außenwirkung",
  geld: "Seite mit Geld",
  fremd: "fremder Text auf der Seite",
  kosten: "Kosten nicht lesbar",
  budget: "Kostendeckel erreicht",
  tageslimit: "Tageslimit erreicht",
  deckel: "Rundengrenze erreicht",
  "anderer-zug": "ein neuer Auftrag kam dazwischen",
  neustart: "Server neu gestartet",
  aus: "abgeschaltet",
  anmeldung: "Anmeldung nicht erkannt",
  start: "Start kam nicht in Gang",
  sonst: "beendet",
});

/** Die Uhrzeit „07:47“ — die Seite gibt ihre eigene Formatierung hinein (Tests: fest). */
export type UhrFormat = (ms: number) => string;

export const uhrDe: UhrFormat = (ms) => new Date(ms).toLocaleTimeString("de-AT", { hour: "2-digit", minute: "2-digit" });

function dauerWort(min: number): string {
  if (min < 60) return `${min} Min`;
  const std = Math.floor(min / 60);
  const rest = min % 60;
  return rest ? `${std} Std ${rest} Min` : `${std} Std`;
}

function restSatz(min: number): string {
  return `noch ${dauerWort(min)}`;
}

/** Der Hauptsatz: „Läuft · noch 41 Min · letzter Schritt: AWS-Seite geprüft“, „Abgelaufen um 07:47 – Wartezeit zählte mit“. */
export function laufSatz(b: LaufBlick, uhr: UhrFormat = uhrDe): string {
  const seite = b.art ? SEITE_JE_ART[b.art] : "Seite";
  const rest = b.restMin !== null ? restSatz(b.restMin) : null;
  const schritt = b.letzterSchritt ? `letzter Schritt: ${SCHRITT_SATZ[b.letzterSchritt](seite)}` : null;
  const grund = b.grund ? GRUND_SATZ[b.grund] : null;
  const um = b.endeMs !== null ? ` um ${uhr(b.endeMs)}` : "";
  const teile = (...t: (string | null)[]): string => t.filter((x): x is string => !!x).join(" · ");
  switch (b.stand) {
    case "arbeitet":
      return teile("Läuft", rest, schritt ?? "startet");
    case "wartet-auf-martin":
      return teile("Wartet auf dich", grund, rest);
    case "wartet-auf-anmeldung":
      // B12b: „Wartet auf deine Anmeldung · seit 12 Min“ — die Restzeit steht still (Wartezeit zählt nicht).
      return teile("Wartet auf deine Anmeldung", b.seitMin !== null ? `seit ${dauerWort(b.seitMin)}` : null, rest);
    case "limit":
      return teile("Pausiert", grund, rest);
    case "abgelaufen":
      return `Abgelaufen${um} – ${GRUND_SATZ.zeit}`;
    case "gestoppt":
      return b.grund === "stopp" || !grund ? `Gestoppt${um}` : `Beendet${um} – ${grund}`;
    case "fertig":
      return `Fertig${um} – ${GRUND_SATZ.fertig}`;
    case "kein-lauf":
      return "Kein Lauf aktiv";
  }
}

/** „1,20 €“ — aus Cent. Die Buchung liefert Kommazahl-Cent (USD × Kurs), daher erst auf ganze Cent runden. */
export function euro(cent: number): string {
  const c = Math.max(0, Math.round(cent));
  return `${Math.floor(c / 100)},${String(c % 100).padStart(2, "0")} €`;
}

/** Die Nebenzeile: Stufe, Schritte, Kosten, letzte Bewegung — leer bei „kein Lauf“. */
export function laufDetail(b: LaufBlick, uhr: UhrFormat = uhrDe): string {
  if (b.stand === "kein-lauf") return "";
  const t: string[] = [];
  t.push(b.stufe ? `Mandat ${MANDAT_STUFEN_WERTE[b.stufe].titel}` : "Prozess");
  t.push(b.schritte === 1 ? "1 Schritt" : `${b.schritte} Schritte`);
  if (b.kostenCent !== null) t.push(b.budgetCent ? `Kosten ${euro(b.kostenCent)} von ${euro(b.budgetCent)}` : `Kosten ${euro(b.kostenCent)}`);
  if (b.letzteMs !== null && b.endeMs === null) t.push(`zuletzt ${uhr(b.letzteMs)}`);
  return t.join(" · ");
}

/** Hat der Stand einen Stopp-Knopf? Nur solange etwas besteht, das sich stoppen lässt. */
export function laufStoppbar(stand: LaufAnsichtStand): boolean {
  return stand === "arbeitet" || stand === "wartet-auf-martin" || stand === "wartet-auf-anmeldung" || stand === "limit";
}

// ---------------------------------------------------------------------------------------------------------------------------------
// Form prüfen (die Seite nimmt nur die erwartete Antwort an)
// ---------------------------------------------------------------------------------------------------------------------------------

const ist = <T extends string>(liste: readonly T[], x: unknown): x is T => typeof x === "string" && (liste as readonly string[]).includes(x);
const zahlOderNull = (x: unknown): x is number | null => x === null || (typeof x === "number" && Number.isFinite(x) && x >= 0);

/** Die Form aus der Antwort — genau die erwarteten Felder und Kürzel, sonst `null`. */
export function leseLaufBlick(roh: unknown): LaufBlick | null {
  if (!roh || typeof roh !== "object" || Array.isArray(roh)) return null;
  const o = roh as Record<string, unknown>;
  const felder = Object.keys(KEIN_LAUF);
  if (Object.keys(o).length !== felder.length || !felder.every((f) => Object.hasOwn(o, f))) return null;
  if (o.v !== 1 || !ist(LAUF_STAENDE, o.stand)) return null;
  if (o.stufe !== null && !ist(MANDAT_STUFEN, o.stufe)) return null;
  if (o.art !== null && !ist(MANDAT_ARTEN, o.art)) return null;
  if (o.letzterSchritt !== null && !ist(LAUF_SCHRITTE, o.letzterSchritt)) return null;
  if (o.grund !== null && !ist(LAUF_GRUENDE, o.grund)) return null;
  if (typeof o.schritte !== "number" || !Number.isInteger(o.schritte) || o.schritte < 0) return null;
  for (const f of ["restMin", "kostenCent", "budgetCent", "letzteMs", "endeMs", "seitMin"] as const) if (!zahlOderNull(o[f])) return null;
  return {
    v: 1,
    stand: o.stand,
    stufe: o.stufe,
    art: o.art,
    restMin: o.restMin as number | null,
    schritte: o.schritte,
    letzterSchritt: o.letzterSchritt,
    kostenCent: o.kostenCent as number | null,
    budgetCent: o.budgetCent as number | null,
    letzteMs: o.letzteMs as number | null,
    endeMs: o.endeMs as number | null,
    grund: o.grund,
    seitMin: o.seitMin as number | null,
  };
}
